/**
 * Calls poll every interval. While the tab is hidden, polls less often, or
 * pauses without hiddenInterval. soon() polls earlier. Only one poll runs at
 * a time
 */
export default class Poller {
  /**
   * @param {() => Promise<unknown>} poll
   * @param {object} options
   * @param {number} options.interval
   * @param {number} [options.hiddenInterval] how often while the tab is
   * hidden. Without it, polling pauses while the tab is hidden
   * @param {number} [options.soonDelay] the delay of a poll asked by soon()
   */
  constructor(poll, { interval, hiddenInterval, soonDelay = 0 }) {
    this.poll = poll;
    this.interval = interval;
    this.hiddenInterval = hiddenInterval;
    this.soonDelay = soonDelay;
    this.timer = 0;
    this.stopped = false;
    // soon() scheduled the next poll. Showing or hiding the tab keeps it
    this.isSoon = false;
    this.polling = false;
    // A poll came due while another ran. It runs right after
    this.pollAgain = false;
    this.lastPoll = 0;
  }

  start() {
    this.stopped = false;
    this.lastPoll = Date.now();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.scheduleNext();
  }

  soon() {
    this.schedule(this.soonDelay, true);
  }

  stop() {
    this.stopped = true;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    clearTimeout(this.timer);
    this.timer = 0;
  }

  get paused() {
    return document.hidden && !this.hiddenInterval;
  }

  // Reschedules from the last poll, so a tab shown after a long time polls
  // at once
  onVisibilityChange = () => {
    // A running poll schedules the next one itself
    if (this.isSoon || this.polling) {
      return;
    }

    // Without hiddenInterval, polling paused while the tab was hidden, so it
    // polls right away when the tab is shown
    if (!document.hidden && !this.hiddenInterval) {
      this.schedule(0);
    } else {
      this.scheduleNext();
    }
  };

  scheduleNext() {
    if (this.paused) {
      clearTimeout(this.timer);
      this.timer = 0;
      return;
    }

    const interval = document.hidden ? this.hiddenInterval : this.interval;

    this.schedule(Math.max(0, this.lastPoll + interval - Date.now()));
  }

  schedule(delay, isSoon = false) {
    if (this.stopped) {
      return;
    }

    clearTimeout(this.timer);
    this.isSoon = isSoon;

    this.timer = setTimeout(async () => {
      this.timer = 0;
      this.isSoon = false;

      if (this.polling) {
        this.pollAgain = true;
        return;
      }

      // Paused until the tab is shown
      if (this.paused) {
        return;
      }

      this.polling = true;

      try {
        await this.poll();
      } catch (err) {
        // The next poll tries again
        console.error(err);
      }

      this.polling = false;
      this.lastPoll = Date.now();

      if (this.pollAgain) {
        this.pollAgain = false;
        this.schedule(0);
      } else if (!this.timer) {
        // Unless soon() scheduled a poll while this one ran
        this.scheduleNext();
      }
    }, delay);
  }
}
